import { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Card,
  HStack,
  VStack,
  Stack,
  Text,
  IconButton,
  Button,
  Separator,
  FileUpload,
  Heading,
  Stat,
  Status,
} from '@chakra-ui/react'
import { Tooltip } from '@/components/ui/tooltip'
import {
  LuCalendarRange,
  LuChevronDown,
  LuChevronUp,
  LuInfo,
  LuPaperclip,
  LuUpload,
} from 'react-icons/lu'
import { toast } from '@/components/ui/toaster/toasterHelpers'
import {
  ATTACHMENT_ACCEPT,
  MAX_ATTACHMENT_FILES,
  MAX_ATTACHMENT_FILE_SIZE,
  translateAttachmentError,
} from '@/common/attachments'
import { getErrorMessage } from '@/helper/apiError.helpers'
import { uploadOrderAttachment, listOrderAttachments } from '@/api/order-attachments.api'
import { CancelCorrectionDialog } from '@/components/modals/CancelCorrectionDialog'
import { useDisclosure } from '@chakra-ui/react'
import { DeleteAttachmentDialog } from '../modals/DeleteAttachmentModal'
import { getOrderHistory } from '@/api/orders.api'
import { ApproveCorrectionDialog } from '../modals/ApproveCorrectionDialog'
import { hoursToUnits, mapAttachmentToFrontend } from '@/helper/order.helper'
import { type OrderAttachmentFileDetails, type OrderHistory } from '@/common/order.common'

export type OrderStatus =
  | 'ABGESENDET'
  | 'WIRD_UEBERPRUEFT'
  | 'KORREKTUR'
  | 'KORREKTUR_AKZEPTIERT'
  | 'KORREKTUR_ABGELEHNT'
  | 'FREIGEGEBEN'
  | 'ABGELEHNT'
  | 'IN_BEARBEITUNG'

export interface Quantities {
  toj?: number
  beratung?: number
  schulung?: number
  pilot?: number
}

export interface CorrectionInfo {
  unionComment?: string
  updated?: Quantities
  commissioned?: Quantities
}

export interface OrderItemProps {
  orderId: number
  bankName: string
  periodText: string
  status: OrderStatus
  defaultExpanded?: boolean
  /** Archived orders are read-only: no attachment upload or deletion. */
  readOnly?: boolean
  ordered?: Quantities
  exceedsCapacity?: boolean
  attachmentCount?: number
  correction?: CorrectionInfo
  onRejectCorrection?: () => void
  onAcceptCorrection?: () => void
  onAfterDelete?: () => void
  existingFiles?: {
    id: number
    name: string
    contentType?: string
    url?: string
  }[]
  onToggleExpand?: (expanded: boolean, ordered?: Quantities) => void
  onAttachmentCountChange?: (orderId: number, count: number) => void
}

function StatusPill({ status }: { status: OrderStatus }) {
  const { label, colorPalette } = useMemo(() => {
    switch (status) {
      case 'ABGESENDET':
        return { label: 'Abgesendet', colorPalette: 'yellow' }
      case 'WIRD_UEBERPRUEFT':
        return { label: 'Wird überprüft', colorPalette: 'yellow' }
      case 'IN_BEARBEITUNG':
        return { label: 'In Bearbeitung', colorPalette: 'orange' }
      case 'KORREKTUR':
        return { label: 'Korrektur', colorPalette: 'blue' }
      case 'KORREKTUR_AKZEPTIERT':
        return { label: 'Korrektur akzeptiert', colorPalette: 'green' }
      case 'FREIGEGEBEN':
        return { label: 'Freigegeben', colorPalette: 'green' }
      case 'ABGELEHNT':
        return { label: 'Abgelehnt', colorPalette: 'red' }
      case 'KORREKTUR_ABGELEHNT':
        return { label: 'Korrektur abgelehnt', colorPalette: 'red' }
      default:
        return { label: status, colorPalette: 'gray' }
    }
  }, [status])

  return (
    <Status.Root size="md" colorPalette={colorPalette}>
      <Status.Indicator />
      {label}
    </Status.Root>
  )
}
function QtyRow({ title, q }: { title: string; q?: number }) {
  return (
    <Stat.Root minW="20">
      <Stat.Label>{title}</Stat.Label>
      <Stat.ValueText>{q ?? 0}</Stat.ValueText>
    </Stat.Root>
  )
}

function QuantitiesBlock({ quantities }: { quantities?: Quantities }) {
  return (
    <HStack gap={12} wrap="wrap">
      <QtyRow title="TOJ" q={quantities?.toj != null ? hoursToUnits('TOJ', quantities.toj) : 0} />
      <QtyRow
        title="Beratung"
        q={quantities?.beratung != null ? hoursToUnits('CONSULTING', quantities.beratung) : 0}
      />
      <QtyRow
        title="Schulung"
        q={quantities?.schulung != null ? hoursToUnits('TRAINING', quantities.schulung) : 0}
      />
      {(quantities?.pilot ?? 0) > 0 && (
        <QtyRow title="Pilot" q={hoursToUnits('PILOT', quantities!.pilot!)} />
      )}
    </HStack>
  )
}

export default function OrderItem(props: OrderItemProps) {
  const {
    orderId,
    bankName,
    periodText,
    status,
    defaultExpanded,
    readOnly = false,
    ordered,
    exceedsCapacity,
    correction,
    existingFiles = [],
  } = props

  const { attachmentCount = 0 } = props

  // Attachments are read-only for archived orders and for orders that have been
  // approved/rejected: no new uploads and no deletion of existing files.
  const attachmentsLocked = readOnly || ['FREIGEGEBEN', 'ABGELEHNT'].includes(status)
  const {
    open: isDeleteDialogOpen,
    onOpen: openDeleteDialog,
    onClose: closeDeleteDialog,
  } = useDisclosure()

  const [selectedAttachmentId, setSelectedAttachmentId] = useState<number | null>(null)
  const [expanded, setExpanded] = useState<boolean>(Boolean(defaultExpanded))
  const [localFiles, setLocalFiles] = useState<OrderAttachmentFileDetails[]>(existingFiles)
  const [files, setFiles] = useState<File[]>([])
  const MAX_FILES = MAX_ATTACHMENT_FILES
  const MAX_FILE_SIZE = MAX_ATTACHMENT_FILE_SIZE
  const {
    open: isCancelDialogOpen,
    onOpen: openCancelDialog,
    onClose: closeCancelDialog,
  } = useDisclosure()
  const {
    open: isApproveDialogOpen,
    onOpen: openApproveDialog,
    onClose: closeApproveDialog,
  } = useDisclosure()
  const [history, setHistory] = useState<OrderHistory[]>([])
  const toggleExpanded = (next: boolean) => {
    setExpanded(next)
    props.onToggleExpand?.(next, ordered) // ordered = props.ordered (hours)
  }

  useEffect(() => {
    if (!expanded) return

    // attachments
    listOrderAttachments(orderId).then(res => {
      const mapped = res.attachments.map(mapAttachmentToFrontend.bind(null, orderId))
      setLocalFiles(mapped)
      props.onAttachmentCountChange?.(orderId, mapped.length)
    })

    // history
    if (history.length > 0) return
    ;(async () => {
      try {
        const result = await getOrderHistory(orderId)
        setHistory(result)
      } catch (e) {
        console.error('Failed to load order history', e)
      }
    })()
  }, [expanded, orderId, history.length])
  const correctionEntry = useMemo(() => {
    const entries = history.filter(h => h.toStatus === 'CORRECTED' && h.note)
    return entries.at(-1) ?? null // take latest one
  }, [history])
  const rejectEntry = useMemo(() => {
    const entries = history.filter(h => h.toStatus === 'REJECTED' && h.note)
    return entries.at(-1) ?? null
  }, [history])

  const correctionData = useMemo(() => {
    if (!correctionEntry) return null

    const oldBuckets = correctionEntry.payload?.oldBuckets || []
    const newBuckets = correctionEntry.payload?.newBuckets || []

    const mapBuckets = (buckets: any[]): Quantities => ({
      toj: buckets.find(b => b.serviceType === 'TOJ')?.plannedHours ?? 0,
      beratung: buckets.find(b => b.serviceType === 'CONSULTING')?.plannedHours ?? 0,
      schulung: buckets.find(b => b.serviceType === 'TRAINING')?.plannedHours ?? 0,
      pilot: buckets.find(b => b.serviceType === 'PILOT')?.plannedHours ?? 0,
    })

    return {
      original: mapBuckets(oldBuckets),
      corrected: mapBuckets(newBuckets),
    }
  }, [correctionEntry])

  //Helper function for checking if the order was correct
  const wasCorrectedAndApproved = useMemo(() => {
    const hadCorrection = history.some(h => h.toStatus === 'CORRECTED')

    const acceptedCorrection = history.some(
      h => h.fromStatus === 'CORRECTED' && h.toStatus === 'SUBMITTED'
    )

    const finallyApproved = history.some(h => h.toStatus === 'APPROVED')

    return hadCorrection && acceptedCorrection && finallyApproved
  }, [history])

  return (
    <Card.Root borderColor="border" w="full" minW="0" maxW="100%">
      <Card.Body>
        {/* Header */}
        <Stack
          direction={{ base: 'column', lg: 'row' }}
          justify="space-between"
          align="center"
          w="full"
          onClick={() => {
            if (!expanded) toggleExpanded(true) // only expand
          }}
          cursor={expanded ? 'default' : 'pointer'}
        >
          <Stack
            direction={{ base: 'column', xl: 'row' }}
            flex="2"
            width="full"
            color="fg.muted"
            align={{ base: 'start', xl: 'center' }}
            justify="space-between"
            gap={2}
          >
            <Card.Title truncate="true" color="fg">
              {bankName}
            </Card.Title>
            <HStack
              // minW="70px" // <--- THIS is the magic
              justify="flex-end"
              align={{ base: 'center', md: 'start' }}
              gap="1"
              pt="1"
            >
              <Box as={LuCalendarRange} verticalAlign="middle" flexShrink={0} />
              <Text textStyle="metadata" color="fg.muted" whiteSpace="nowrap">
                {periodText}
              </Text>
            </HStack>
          </Stack>
          <HStack flex="1" width="full" justify="space-between">
            <HStack
              gap={1}
              justify={{ base: 'start', md: 'center' }}
              align="center"
              minW="48px"
              h="24px"
            >
              <LuPaperclip size={14} />
              <Text textStyle="metadata">{attachmentCount}</Text>
            </HStack>
            {/* Right side: status + chevron */}
            <HStack gap={0.5} align="center">
              <StatusPill status={wasCorrectedAndApproved ? 'KORREKTUR_AKZEPTIERT' : status} />
              <IconButton
                aria-label={expanded ? 'Einklappen' : 'Ausklappen'}
                variant="ghost"
                onClick={e => {
                  e.stopPropagation() // prevent triggering the parent click
                  toggleExpanded(!expanded)
                }}
              >
                {expanded ? <LuChevronUp /> : <LuChevronDown />}
              </IconButton>
            </HStack>
          </HStack>
        </Stack>

        {/* Expanded content */}
        {expanded && (
          <VStack w="full" gap={4} align="stretch" mt={2}>
            {[
              'ABGESENDET',
              'WIRD_UEBERPRUEFT',
              'IN_BEARBEITUNG',
              'FREIGEGEBEN',
              'KORREKTUR',
              'KORREKTUR_AKZEPTIERT',
              'KORREKTUR_ABGELEHNT',
              'ABGELEHNT',
            ].includes(status) && (
              <VStack align="start" gap={3}>
                <HStack gap={3.5} color="fg.muted">
                  <Heading size="xl" color="fg.muted">
                    Bestellte Leistungen
                  </Heading>
                  <Tooltip content="Angeforderte Stunden pro Leistungstyp">
                    <Box as={LuInfo} boxSize="12px" />
                  </Tooltip>
                </HStack>
                <QuantitiesBlock quantities={correctionData?.original ?? ordered} />

                <Separator />
                {/* --- Attachments Section --- */}
                {!attachmentsLocked && (
                  <Box mt={4}>
                    <Heading size="xl" mb={2}>
                      Anhänge
                    </Heading>

                    <FileUpload.Root
                      maxFiles={MAX_FILES}
                      maxFileSize={MAX_FILE_SIZE} // 80 MB per file
                      accept={ATTACHMENT_ACCEPT}
                      onFileAccept={e => {
                        const acceptedFiles = e.files
                          .map((f: any) => {
                            // Case 1: Chakra returns raw File directly
                            if (f instanceof File) return f

                            // Case 2: Chakra returns wrapper object
                            if (f?.file instanceof File) return f.file

                            // Case 3: Blob → convert to File
                            if (f?.file instanceof Blob) {
                              return new File([f.file], f.name || f.file.name || 'upload.dat', {
                                type: f.file.type || 'application/octet-stream',
                              })
                            }

                            return null
                          })
                          .filter(Boolean)

                        if (acceptedFiles.length === 0) {
                          toast.error(
                            'Ungültige Datei',
                            'Die Datei konnte nicht verarbeitet werden.'
                          )
                          return
                        }

                        setFiles(acceptedFiles)

                        acceptedFiles.forEach(async file => {
                          try {
                            const res = await uploadOrderAttachment(orderId, file)

                            const mapped = res.attachments.map(
                              mapAttachmentToFrontend.bind(null, orderId)
                            )

                            setLocalFiles(mapped)

                            // 🔥 UPDATE PARENT COUNTER
                            props.onAttachmentCountChange?.(orderId, mapped.length)

                            toast.success('Upload erfolgreich', `${file.name} wurde hochgeladen.`)
                          } catch (error) {
                            console.error('Failed to upload order attachment', error)
                            toast.error(
                              'Upload fehlgeschlagen',
                              `${file.name} konnte nicht hochgeladen werden: ${translateAttachmentError(
                                getErrorMessage(error, 'Unbekannter Fehler')
                              )}`,
                              { duration: 8000 }
                            )
                          }
                        })
                      }}
                      onFileReject={e => {
                        const rejectedNames = e.files.map(f => f.file.name).join(', ')
                        toast.error(
                          'Ungültige Datei',
                          `Die folgenden Dateien werden nicht unterstützt: ${rejectedNames}`
                        )
                      }}
                    >
                      <FileUpload.HiddenInput multiple />
                      <FileUpload.Trigger asChild>
                        <Button variant="outline">
                          <LuUpload /> Anhang hinzufügen
                        </Button>
                      </FileUpload.Trigger>

                      {/* Preview list with size + clear buttons */}
                      {files.length > 0 && (
                        <Box w="full" alignSelf="stretch">
                          <Text textStyle="xsMedium" mb={2}>
                            Anhänge verwalten
                          </Text>
                          <FileUpload.List showSize />
                        </Box>
                      )}
                    </FileUpload.Root>
                    {/* Show helper text only when files exist */}
                    {files.length > 0 && (
                      <HStack align="center" gap={2} mt={2}>
                        <LuInfo size="10px" />
                        <Text textStyle="2xsMedium" color="fg.muted">
                          Sie können bis zu {MAX_FILES} Anhänge mit einer Größe von jeweils bis zu{' '}
                          {Math.round(MAX_FILE_SIZE / (1024 * 1024))} MB hochladen.
                        </Text>
                      </HStack>
                    )}
                  </Box>
                )}
                {localFiles.length > 0 && (
                  <Box mt={4}>
                    <Text textStyle="xsMedium" mb={2}>
                      Bereits vorhandene Anhänge
                    </Text>
                    <VStack align="start" gap={1}>
                      {localFiles.map(file => (
                        <Card.Root key={file.id} w="full">
                          <Card.Body flexDirection="row" justifyContent="space-between">
                            {/* LEFT SIDE: Filename */}
                            <Text textStyle="body" color="fg">
                              {file.name}
                            </Text>

                            {/* RIGHT SIDE: Anzeigen + Löschen */}
                            <HStack gap={1}>
                              <Button asChild variant="ghost">
                                <a href={file.url} target="_blank" rel="noopener noreferrer">
                                  Anzeigen
                                </a>
                              </Button>
                              {!attachmentsLocked && (
                                <Button
                                  variant="ghost"
                                  onClick={() => {
                                    setSelectedAttachmentId(file.id)
                                    openDeleteDialog()
                                  }}
                                >
                                  Löschen
                                </Button>
                              )}
                            </HStack>
                          </Card.Body>
                        </Card.Root>
                      ))}
                    </VStack>
                  </Box>
                )}
              </VStack>
            )}

            {status === 'IN_BEARBEITUNG' && exceedsCapacity && (
              <Alert.Root status="warning" variant="subtle">
                <Alert.Indicator />
                <Alert.Content>
                  <Alert.Title>Achtung</Alert.Title>
                  <Alert.Description>
                    Vermittelte Leistungen reichen über das vereinbarte Leistungskontingent hinaus.
                    Dies könnte ein Ablehnungsgrund für die Union Investment darstellen.
                  </Alert.Description>
                </Alert.Content>
              </Alert.Root>
            )}

            {(['KORREKTUR', 'KORREKTUR_AKZEPTIERT', 'KORREKTUR_ABGELEHNT'].includes(status) ||
              wasCorrectedAndApproved) && (
              <VStack align="stretch" gap={4}>
                <Separator />
                <VStack align="stretch" gap={3.5}>
                  <HStack gap={2} color="fg.muted">
                    <Heading size="xl">Korrektur durch die Union Investment</Heading>
                    <Tooltip content="Kommentar und neue Verteilung">
                      <Box as={LuInfo} boxSize="12px" />
                    </Tooltip>
                  </HStack>
                  {correctionEntry?.note && (
                    <Card.Root>
                      <Card.Body>
                        <Card.Title>Kommentar durch die Union Investment</Card.Title>
                        <Card.Description>{correctionEntry.note}</Card.Description>
                      </Card.Body>
                    </Card.Root>
                  )}
                </VStack>

                <VStack align="start" gap={3}>
                  <HStack gap={3.5} color="fg.muted">
                    <Heading size="xl">Sie bearbeiten folgende Leistungen</Heading>
                    <Tooltip content="Ihnen zugewiesene Stunden nach Korrektur">
                      <Box as={LuInfo} boxSize="12px" />
                    </Tooltip>
                  </HStack>
                  <QuantitiesBlock quantities={correctionData?.corrected} />
                </VStack>

                {correction?.commissioned && (
                  <VStack align="stretch" gap={3}>
                    <HStack gap={2} color="fg.muted">
                      <Heading size="xl">
                        Folgende Leistungen werden durch eine andere Person bearbeitet
                      </Heading>
                      <Tooltip content="Kommissionierte Stunden an anderen SIB">
                        <Box as={LuInfo} boxSize="12px" />
                      </Tooltip>
                    </HStack>
                    <QuantitiesBlock quantities={correction.commissioned} />
                    <Alert.Root status="neutral" variant="subtle">
                      <Alert.Indicator />
                      <Alert.Content>
                        <Alert.Title>Vermittlungszuschlag</Alert.Title>
                        <Alert.Description>
                          Für alle von Ihnen bestellten Leistungen, die infolge der Korrektur von
                          einer anderen Person bearbeitet werden, erhalten Sie weiterhin den
                          Vermittlungszuschlag.
                        </Alert.Description>
                      </Alert.Content>
                    </Alert.Root>
                  </VStack>
                )}
                {status === 'KORREKTUR' && (
                  <HStack justify="start" gap={4} pt={2} wrap="wrap">
                    <Button variant="subtle" colorPalette="red" onClick={openCancelDialog}>
                      Korrektur ablehnen
                    </Button>
                    <Button variant="subtle" colorPalette="green" onClick={openApproveDialog}>
                      Korrektur verbindlich akzeptieren
                    </Button>
                  </HStack>
                )}
              </VStack>
            )}

            {status === 'ABGELEHNT' && (
              <VStack align="start" gap={2}>
                <Separator />

                <Card.Root w="full">
                  <Card.Body>
                    <Card.Title>Kommentar der Union Investment</Card.Title>
                    <Card.Description>
                      {rejectEntry?.note ?? 'Kein Kommentar vorhanden.'}
                    </Card.Description>
                  </Card.Body>
                </Card.Root>
              </VStack>
            )}
          </VStack>
        )}

        <CancelCorrectionDialog
          isOpen={isCancelDialogOpen}
          onClose={closeCancelDialog}
          orderId={orderId}
          onDeleted={props.onAfterDelete}
        />
        <DeleteAttachmentDialog
          isOpen={isDeleteDialogOpen}
          onClose={closeDeleteDialog}
          orderId={orderId}
          attachmentId={selectedAttachmentId}
          onDeleted={async () => {
            const updated = await listOrderAttachments(orderId)
            const mapped = updated.attachments.map(mapAttachmentToFrontend.bind(null, orderId))

            setLocalFiles(mapped)
            props.onAttachmentCountChange?.(orderId, mapped.length)
          }}
        />
        <ApproveCorrectionDialog
          isOpen={isApproveDialogOpen}
          onClose={closeApproveDialog}
          orderId={orderId}
          onAccepted={async () => {
            props.onAcceptCorrection?.() // parent handler
            props.onAfterDelete?.() // reuse refresh callback
          }} // pass refresh handler
        />
      </Card.Body>
    </Card.Root>
  )
}
